import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useHistory } from 'react-router';
import { StyledButton } from "components/Buttons/DefaultButton/DefaultButton.styles";
import { SimpleModal } from '../../../../components/Modal';
import { CircularProgress } from '@material-ui/core';
import styles from '../CustomOfferDetailsPage.module.scss';
import Api from 'api';
import { getOfficeCancellationErrorMessage } from 'utils/officeCancellationError';

interface CancelShiftButtonProps {
    job: {
        id: number;
        hygienistId?: number;
    };
}

const CancelShiftButton: React.FC<CancelShiftButtonProps> = ({ job }) => {
    const history = useHistory();
    const submitting = useRef(false);
    const mounted = useRef(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    const handleRemoveShift = useCallback(() => {
        setError(null);
        setIsModalOpen(true);
    }, []);

    const cancelJob = useCallback(async () => {
        if (submitting.current) return;
        submitting.current = true;
        setIsLoading(true);
        setError(null);
        try {
            await Api.removeUnbookedJob(job.id);
            if (!mounted.current) return;
            setIsModalOpen(false);
            history.replace('dentistHome');
        } catch (err) {
            if (mounted.current) setError(getOfficeCancellationErrorMessage(err));
        } finally {
            submitting.current = false;
            if (mounted.current) setIsLoading(false);
        }
    }, [history, job.id]);

    const cancelMessage = job.hygienistId
        ? <>Are you sure?<br/><br/>Every hygienist who has submitted an offer will be notified.</>
        : 'Are you sure?';

    return (
        <>
            <StyledButton
                variant="outlined"
                color="error"
                size="small"
                data-testid="offer-details-remove-shift-button"
                className={styles.removeShiftButton}
                onClick={handleRemoveShift}
            >
                Remove Shift
            </StyledButton>
            <SimpleModal
                isOpen={isModalOpen}
                onClose={() => { if (!submitting.current) setIsModalOpen(false); }}
                disableBackdropClose={isLoading}
                disableEscapeKeyDown={isLoading}
            >
                <h2>Remove this shift</h2>
                <div>{cancelMessage}</div>
                {isLoading && <CircularProgress />}
                {error && <p role="alert" style={{ color: 'red' }}>{error}</p>}
                <div>
                    <StyledButton
                        variant="outlined"
                        data-testid="offer-details-remove-shift-no"
                        disabled={isLoading}
                        onClick={() => setIsModalOpen(false)}
                    >
                        No
                    </StyledButton>
                    <StyledButton
                        variant="contained"
                        color="primary"
                        data-testid="offer-details-remove-shift-yes"
                        onClick={cancelJob}
                        disabled={isLoading}
                    >
                        Yes
                    </StyledButton>
                </div>
            </SimpleModal>
        </>
    );
};

export default CancelShiftButton;
